import type { SVGProps } from "react";

export type ParsedColor = {
  r: number;
  g: number;
  b: number;
};

export function parseColor(color?: string): ParsedColor | null {
  if (!color) return null;

  const normalized = color.trim();
  if (normalized.startsWith("#")) {
    const hex = normalized.slice(1);
    const fullHex = hex.length === 3 ? hex.split("").map((char) => char + char).join("") : hex.slice(0, 6);
    if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return null;

    return {
      r: Number.parseInt(fullHex.slice(0, 2), 16),
      g: Number.parseInt(fullHex.slice(2, 4), 16),
      b: Number.parseInt(fullHex.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
}

export function withAlpha(color: string | undefined, alpha: number, fallback = "hsl(var(--primary) / 0.16)") {
  const parsed = parseColor(color);
  if (!parsed) return fallback;
  return `rgba(${parsed.r}, ${parsed.g}, ${parsed.b}, ${alpha})`;
}

export function getContrastTextColor(color?: string) {
  const parsed = parseColor(color);
  if (!parsed) return "hsl(var(--foreground))";

  const luminance = (parsed.r * 299 + parsed.g * 587 + parsed.b * 114) / 1000;
  return luminance > 160 ? "#111827" : "#FFFFFF";
}

export function splitLabel(label: string, maxCharsPerLine = 12, maxLines = 3) {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxCharsPerLine || !current) {
      current = candidate;
      return;
    }

    lines.push(current);
    current = word;
  });

  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;

  return [
    ...lines.slice(0, maxLines - 1),
    `${lines.slice(maxLines - 1).join(" ").slice(0, Math.max(0, maxCharsPerLine - 1))}…`,
  ];
}

type SvgMultilineTextProps = SVGProps<SVGTextElement> & {
  x: number;
  y: number;
  label: string;
  fill: string;
  fontSize?: number;
  maxCharsPerLine?: number;
};

export function SvgMultilineText({
  x,
  y,
  label,
  fill,
  fontSize = 11,
  maxCharsPerLine = 12,
  ...props
}: SvgMultilineTextProps) {
  const lines = splitLabel(label, maxCharsPerLine);
  const lineHeight = fontSize + 2;
  const startY = y - ((lines.length - 1) * lineHeight) / 2;

  return (
    <text x={x} y={startY} textAnchor="middle" fontSize={fontSize} fontWeight="700" fill={fill} dominantBaseline="middle" {...props}>
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={x} dy={index === 0 ? 0 : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}